import { useCallback, useMemo, useState } from 'react';

export type ChartSelectionState = {
  /** Set of currently selected keys. Empty set = all visible. */
  selectedKeys: Set<string>;
  /** Whether at least one key is explicitly selected. */
  hasSelection: boolean;
  /** Returns true if the given key is selected, or if no selection is active. */
  isVisible: (key: string) => boolean;
  /** Toggle a key: add if absent, remove if present. */
  toggleKey: (key: string) => void;
  /** Replace the current selection with the given keys. */
  setSelectedKeys: (keys: string[]) => void;
  /** Clear the selection (show all). */
  clearAll: () => void;
  /** Select all the given keys at once. */
  selectAll: (keys: string[]) => void;
};

/**
 * Standalone hook that manages chart series selection state.
 *
 * Use this when you need to drive a `ChartSelectionProvider` from outside,
 * e.g. from a table or a custom summary block above the chart.
 *
 * ```tsx
 * const selection = useChartSelectionState();
 *
 * <ChartSelectionProvider
 *   selectedKeys={Array.from(selection.selectedKeys)}
 *   onSelectionChange={selection.setSelectedKeys}
 * >
 *   <ChartContainer ...>
 *     <Line selectionKey="cpu" ... />
 *     <Line selectionKey="memory" ... />
 *   </ChartContainer>
 * </ChartSelectionProvider>
 * ```
 */
export const useChartSelectionState = (
  initialSelectedKeys?: string[]
): ChartSelectionState => {
  const [selectedKeys, setSelectedKeysState] = useState<Set<string>>(
    () => new Set(initialSelectedKeys ?? [])
  );

  const toggleKey = useCallback((key: string) => {
    setSelectedKeysState((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const setSelectedKeys = useCallback((keys: string[]) => {
    setSelectedKeysState(new Set(keys));
  }, []);

  const clearAll = useCallback(() => {
    setSelectedKeysState(new Set());
  }, []);

  const selectAll = useCallback((keys: string[]) => {
    setSelectedKeysState(new Set(keys));
  }, []);

  const hasSelection = selectedKeys.size > 0;

  const isVisible = useCallback(
    (key: string) => !hasSelection || selectedKeys.has(key),
    [hasSelection, selectedKeys]
  );

  return useMemo(
    () => ({
      selectedKeys,
      hasSelection,
      isVisible,
      toggleKey,
      setSelectedKeys,
      clearAll,
      selectAll,
    }),
    [
      selectedKeys,
      hasSelection,
      isVisible,
      toggleKey,
      setSelectedKeys,
      clearAll,
      selectAll,
    ]
  );
};
